import * as SecureStore from 'expo-secure-store';
import { useEffect, useCallback, useReducer } from 'react';
import { Platform } from 'react-native';

type UseStateHook<T> = [[boolean, T | null], (value: T | null) => void];
type Listener = (value: string | null) => void;

const listeners = new Map<string, Set<Listener>>();

function emitChange(key: string, value: string | null) {
	listeners.get(key)?.forEach(fn => fn(value));
}

function subscribe(key: string, fn: Listener) {
	if (!listeners.has(key)) listeners.set(key, new Set());
	listeners.get(key)!.add(fn);
	return () => { listeners.get(key)?.delete(fn); };
}

function useAsyncState<T>(
	initialValue: [boolean, T | null] = [true, null],
): UseStateHook<T> {
	return useReducer(
		(state: [boolean, T | null], action: T | null = null): [boolean, T | null] => [false, action],
		initialValue,
	) as UseStateHook<T>;
}

export async function setStorageItemAsync(key: string, value: string | null) {
	if (!key || !/^[a-zA-Z0-9._-]+$/.test(key)) {
		console.trace(`[SecureStore] Invalid key passed to setStorageItemAsync: "${key}"`);
		return;
	}
	if (Platform.OS === 'web') {
		try {
			if (value === null) {
				localStorage.removeItem(key);
			} else {
				localStorage.setItem(key, value);
			}
		} catch (e) {
			console.error('Local storage is unavailable:', e);
		}
	} else {
		if (value == null) {
			await SecureStore.deleteItemAsync(key);
		} else {
			await SecureStore.setItemAsync(key, value);
		}
	}
}

export function useStorageState(key: string): UseStateHook<string> {
	// Public
	const [state, setState] = useAsyncState<string>();

	// Get
	useEffect(() => {
		let cancelled = false;

		if (!key || !/^[a-zA-Z0-9._-]+$/.test(key)) {
			console.trace(`[SecureStore] Invalid key passed to useStorageState: "${key}"`);
			setState(null);
			return;
		}

		if (Platform.OS === 'web') {
			try {
				if (typeof localStorage !== 'undefined') {
					setState(localStorage.getItem(key));
				}
			} catch (e) {
				console.error('Local storage is unavailable:', e);
			}
		} else {
			SecureStore.getItemAsync(key).then(value => {
				if (!cancelled) setState(value);
			});
		}

		return () => {
			cancelled = true;
		};
	}, [key, setState]);

	// Sync across components using the same key
	useEffect(() => subscribe(key, (v) => setState(v)), [key, setState]);

	// Set
	const setValue = useCallback(
		(value: string | null) => {
			setState(value);
			setStorageItemAsync(key, value);
			emitChange(key, value);
		},
		[key, setState],
	);

	return [state, setValue];
}
